var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { Container, Grid, Typography, Box } from '@mui/material';
import { format } from 'date-fns';
import { FormikSelectField } from '../atoms/SelectField';
import TextField, { FormikTextField } from '../atoms/TextField';
import { FormikSingleCheckbox } from '../atoms/SingleCheckbox';
import FormikSelectCountryCallingCodeField from '../atoms/FormikSelectCountryCallingCodeField';
import Button from '../atoms/BaseButton';
import PageSubmitButton from '../molecules/PageSubmitButton';
import ConfirmationDialog from './ConfirmationDialog';
import { formatMoneyAmount } from '../../utils/format';
import { OrderFieldFormats } from '../../constants/fieldFormats';
import { useNotify } from '../../hooks/notificationHook';
import { useOrderListItem } from '../../hooks/useListItem';
import { camelize } from '../../utils/camelize';
import { colors } from '../../constants/styles';
import { okagesamaNoMoriUrls } from '../../constants/contributeToOkagesamaNoMori';
import RoasterAddressSelect from '../organisms/RoasterAddress/RoasterAddressSelect';
import DeliveryRequestItemsAndRequestDateForm from './DeliveryRequestItemsAndRequestDateForm';
import OrderFormStepper from './OrderFormStepper';
import { usePaymentTimingExplanation } from './utils';
var Form = styled.form(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  margin-top: 30px;\n\n  &.hide {\n    display: none;\n  }\n\n  button.countdown {\n    background-color: ", ";\n    color: white;\n  }\n"], ["\n  margin-top: 30px;\n\n  &.hide {\n    display: none;\n  }\n\n  button.countdown {\n    background-color: ", ";\n    color: white;\n  }\n"])), colors.red);
var ExplanationText = styled(Typography).attrs({ variant: 'caption' })(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  white-space: pre-line;\n  word-break: break-word;\n"], ["\n  white-space: pre-line;\n  word-break: break-word;\n"])));
var StyledFormikSelectField = styled(FormikSelectField)(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  & .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline {\n    border-color: red;\n  }\n\n  & .MuiInputLabel-outlined.Mui-error {\n    color: red;\n  }\n"], ["\n  & .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline {\n    border-color: red;\n  }\n\n  & .MuiInputLabel-outlined.Mui-error {\n    color: red;\n  }\n"])));
var REQUIRED_FIELDS = [
    'payment_timing',
    'delivery_method',
    'billing_name',
    'person_name',
    'postal_code',
    'prefecture',
    'city',
    'address',
    'phone',
    'country_calling_code',
    'email',
];
export var FormTypeValues = ['order', 'deliveryRequest'];
var OrderForm = function (_a) {
    var role = _a.role, roaster = _a.roaster, itemType = _a.itemType, orderStartedAt = _a.orderStartedAt, secondsToStartOrder = _a.secondsToStartOrder, warehouseUnitPrice = _a.warehouseUnitPrice, requestItems = _a.requestItems, requestBids = _a.requestBids, _b = _a.showFormType, showFormType = _b === void 0 ? 'order' : _b, onShowFormTypeChange = _a.onShowFormTypeChange, simulatedOrderPrice = _a.simulatedOrderPrice, onPaymentTimingChange = _a.onPaymentTimingChange, isValidOrder = _a.isValidOrder, offerList = _a.offerList;
    var orderFormRef = React.createRef();
    var _c = useTranslation(), t = _c.t, i18n = _c.i18n;
    var notify = useNotify();
    var _d = React.useState(false), confirmDialogOpen = _d[0], setConfirmDialogOpen = _d[1];
    var _e = React.useState('oneTime'), deliveryRequestFormType = _e[0], setDeliveryRequestFormType = _e[1];
    var _f = React.useState({
        address: null,
        itemsAndRequestDates: [],
    }), deliveryRequestGroup = _f[0], setDeliveryRequestGroup = _f[1];
    var _g = React.useState(), requestItemsWithRequestableQuantity = _g[0], setRequestItemsWithRequestableQuantity = _g[1];
    React.useEffect(function () {
        setDeliveryRequestGroup({
            address: null,
            itemsAndRequestDates: [
                {
                    key: 'oneTime',
                    requestDate: null,
                    isFastestDelivery: true,
                    itemsAttributes: requestItems.map(function (requestItem) { return ({
                        orderedItemId: requestItem.orderedItemId,
                        lotNumber: requestItem.lotNumber,
                        quantity: requestItem.quantity,
                        bagSize: requestItem.bagSize,
                    }); }),
                },
            ],
        });
    }, [requestItems]);
    React.useEffect(function () {
        var currentDeliveryRequestGroupItemsAndRequestDates = deliveryRequestGroup.itemsAndRequestDates.filter(function (_a) {
            var key = _a.key;
            return deliveryRequestFormType === 'oneTime'
                ? key === 'oneTime'
                : key !== 'oneTime';
        });
        setRequestItemsWithRequestableQuantity(requestItems.map(function (requestItem) {
            if (requestItem.roasterTransportToWarehousesStatus !== 'warehousing') {
                return __assign(__assign({}, requestItem), { requestableQuantity: 0 });
            }
            var requestedItems = currentDeliveryRequestGroupItemsAndRequestDates
                .map(function (_a) {
                var itemsAttributes = _a.itemsAttributes;
                return itemsAttributes.filter(function (_a) {
                    var orderedItemId = _a.orderedItemId;
                    return orderedItemId === requestItem.orderedItemId;
                });
            })
                .flat();
            return __assign(__assign({}, requestItem), { requestableQuantity: requestItem.quantity -
                    requestedItems.reduce(function (accumulator, _a) {
                        var quantity = _a.quantity;
                        return accumulator + quantity;
                    }, 0) });
        }));
    }, [requestItems, deliveryRequestGroup, deliveryRequestFormType]);
    var deliveryRequestGroupIsValid = React.useMemo(function () {
        if (!onShowFormTypeChange)
            return true;
        if (roaster.isOfficeEurope && !deliveryRequestGroup.vatNumber)
            return false;
        var isAllRequestDatesAreCorrect = deliveryRequestGroup.itemsAndRequestDates
            .filter(function (_a) {
            var key = _a.key;
            return deliveryRequestFormType === 'oneTime'
                ? key === 'oneTime'
                : key !== 'oneTime';
        })
            .every(function (itemAndRequestDate) {
            return ((itemAndRequestDate.isFastestDelivery &&
                itemAndRequestDate.requestDate === null) ||
                (!itemAndRequestDate.isFastestDelivery &&
                    itemAndRequestDate.requestDate !== null));
        });
        if (!isAllRequestDatesAreCorrect)
            return false;
        if (deliveryRequestFormType === 'oneTime') {
            return (deliveryRequestGroup.itemsAndRequestDates.find(function (_a) {
                var key = _a.key;
                return key === 'oneTime';
            }) !== undefined);
        }
        if (!requestItemsWithRequestableQuantity)
            return false;
        return requestItemsWithRequestableQuantity.every(function (_a) {
            var requestableQuantity = _a.requestableQuantity;
            return requestableQuantity === 0;
        });
    }, [
        onShowFormTypeChange,
        deliveryRequestFormType,
        deliveryRequestGroup,
        requestItemsWithRequestableQuantity,
        roaster.isOfficeEurope,
    ]);
    var invalidDeliveryRequestGroupTooltip = React.useMemo(function () {
        if (showFormType !== 'deliveryRequest' || deliveryRequestGroupIsValid)
            return;
        return (React.createElement(React.Fragment, null,
            React.createElement(Typography, { variant: "body1" }, t("pages.typicaSelection.tooltip.invalidDeliveryRequestGroup.".concat(deliveryRequestFormType, ".body1"))),
            React.createElement(Typography, { variant: "body2" }, t("pages.typicaSelection.tooltip.invalidDeliveryRequestGroup.".concat(deliveryRequestFormType, ".body2")))));
    }, [showFormType, deliveryRequestGroupIsValid, deliveryRequestFormType, t]);
    var availablePaymentTimingItems = React.useCallback(function (paymentTimingItems) {
        if (!offerList)
            return [];
        return paymentTimingItems.filter(function (item) {
            return offerList.availablePaymentTimings.includes(item.value);
        });
    }, [offerList]);
    var _h = useOrderListItem(offerList), allPaymentTimingItems = _h.paymentTimingItemsWithHandlingRates, deliveryMethodItems = _h.deliveryMethodItems;
    var paymentTimingItems = React.useMemo(function () {
        var paymentTimingItems = allPaymentTimingItems(itemType, offerList === null || offerList === void 0 ? void 0 : offerList.paymentTimingHandlingRates);
        if (itemType === 'Offer')
            return availablePaymentTimingItems(paymentTimingItems);
        return paymentTimingItems.map(function (item) {
            return [
                'one_time_upfront',
                'one_time_after_imported',
                'one_time_specified_date',
                'lump_sum_payment_after_import',
                'two_times_after_imported',
                'three_times_after_imported',
                'four_times_after_imported',
                'five_times_after_imported',
            ].includes(item.value)
                ? __assign(__assign({}, item), { disabled: true }) : item;
        });
    }, [
        itemType,
        allPaymentTimingItems,
        offerList === null || offerList === void 0 ? void 0 : offerList.paymentTimingHandlingRates,
        availablePaymentTimingItems,
    ]);
    var countdown = React.useMemo(function () {
        if (secondsToStartOrder <= 0)
            return;
        var minutes = "".concat(Math.floor(secondsToStartOrder / 60));
        var seconds = "0".concat(Math.floor(secondsToStartOrder % 60)).slice(-2);
        return "".concat(minutes, ":").concat(seconds);
    }, [secondsToStartOrder]);
    var contributableOkagesamaNoMori = React.useMemo(function () {
        var _a;
        if (!(offerList === null || offerList === void 0 ? void 0 : offerList.contributableOkagesamaNoMori))
            return false;
        return (((_a = offerList === null || offerList === void 0 ? void 0 : offerList.contributeToOkagesamaNoMoris) === null || _a === void 0 ? void 0 : _a.length) || 0) < 1;
    }, [offerList]);
    var onSubmit = React.useCallback(function () {
        if (orderStartedAt && countdown) {
            var startedAt = format(orderStartedAt, 'HH:mm');
            notify('error', [t('messages.orderNotStarted', { startedAt: startedAt })]);
            return;
        }
        setConfirmDialogOpen(true);
    }, [countdown, notify, orderStartedAt, t]);
    var validate = React.useCallback(function (values) {
        var errors = {};
        REQUIRED_FIELDS.forEach(function (field) {
            if (!values[field] || values[field] === '') {
                errors[field] = t('errors.isRequired', {
                    field: t("params.order.".concat(camelize(field))),
                });
            }
        });
        if (['one_time_after_first_delivery', 'after_each_delivery'].includes(values.payment_timing)) {
            if ((simulatedOrderPrice === null || simulatedOrderPrice === void 0 ? void 0 : simulatedOrderPrice.selectableAllPaymentTiming) === false) {
                errors['payment_timing'] = t('errors.notSelectableLessThanOrdersTotalWeight120Kg');
            }
        }
        Object.entries(OrderFieldFormats).forEach(function (_a) {
            var field = _a[0], regExp = _a[1];
            var fieldAsKey = field;
            var value = values[fieldAsKey];
            if (value && !regExp.test(value)) {
                errors[fieldAsKey] = t('errors.invalid', {
                    field: t("params.order.".concat(camelize(field))),
                });
            }
        });
        return errors;
    }, [t, simulatedOrderPrice === null || simulatedOrderPrice === void 0 ? void 0 : simulatedOrderPrice.selectableAllPaymentTiming]);
    var initialSelectedPaymentTiming = React.useMemo(function () {
        if (paymentTimingItems.length === 0)
            return 'one_time_upfront';
        return paymentTimingItems[0].value;
    }, [paymentTimingItems]);
    var formik = useFormik({
        initialValues: {
            payment_timing: itemType === 'Offer'
                ? initialSelectedPaymentTiming
                : 'one_time_after_first_delivery',
            delivery_method: 'bulk',
            billing_name: roaster.company || roaster.storeName || t('fullname', roaster),
            store_name: roaster.storeName || '',
            person_name: t('fullname', roaster),
            name_kana: roaster.nameKana || '',
            postal_code: roaster.postalCode || '',
            prefecture: roaster.prefecture || '',
            city: roaster.city || '',
            address: roaster.address || '',
            building: roaster.building || '',
            phone: roaster.phone || '',
            country_calling_code: roaster.countryCallingCode || '',
            email: roaster.email,
            eori_number: roaster.eoriNumber,
            contribute_to_okagesama_no_mori: '0',
        },
        onSubmit: onSubmit,
        isInitialValid: true,
        validate: validate,
        validateOnChange: true,
        enableReinitialize: true,
    });
    React.useEffect(function () {
        onPaymentTimingChange(formik.values.payment_timing);
    }, [onPaymentTimingChange, formik.values.payment_timing]);
    React.useEffect(function () {
        formik.validateForm();
    }, [simulatedOrderPrice]);
    var changeAddress = React.useCallback(function (address) {
        formik.setValues(__assign(__assign({}, formik.values), { billing_name: address.billingName, store_name: address.storeName, person_name: address.personName, name_kana: address.nameKana, email: address.email, country_calling_code: address.countryCallingCode, phone: address.phone, postal_code: address.postalCode, prefecture: address.prefecture, city: address.city, address: address.address, building: address.building }));
    }, [formik]);
    var handleAddDeliveryRequestItemsAndRequestDate = React.useCallback(function (deliveryRequestGroupItemsAndRequestDate) {
        var newItemsAndRequestDates = deliveryRequestGroup.itemsAndRequestDates.concat([
            deliveryRequestGroupItemsAndRequestDate,
        ]);
        setDeliveryRequestGroup(__assign(__assign({}, deliveryRequestGroup), { itemsAndRequestDates: newItemsAndRequestDates }));
    }, [deliveryRequestGroup, setDeliveryRequestGroup]);
    var handleUpdateDeliveryRequestItemsAndRequestDate = React.useCallback(function (deliveryRequestGroupItemsAndRequestDate) {
        var newItemsAndRequestDates = [];
        deliveryRequestGroup.itemsAndRequestDates.forEach(function (itemAndRequestDate) {
            var newItemAndRequestDate = itemAndRequestDate.key ===
                deliveryRequestGroupItemsAndRequestDate.key
                ? deliveryRequestGroupItemsAndRequestDate
                : itemAndRequestDate;
            newItemsAndRequestDates.push(newItemAndRequestDate);
        });
        setDeliveryRequestGroup(__assign(__assign({}, deliveryRequestGroup), { itemsAndRequestDates: newItemsAndRequestDates }));
    }, [deliveryRequestGroup, setDeliveryRequestGroup]);
    var handleDeleteDeliveryRequestItemsAndRequestDate = React.useCallback(function (deliveryRequestGroupItemsAndRequestDate) {
        var newItemsAndRequestDates = deliveryRequestGroup.itemsAndRequestDates.filter(function (itemsAndRequestDate) {
            return (itemsAndRequestDate.key !==
                deliveryRequestGroupItemsAndRequestDate.key);
        });
        setDeliveryRequestGroup(__assign(__assign({}, deliveryRequestGroup), { itemsAndRequestDates: newItemsAndRequestDates }));
    }, [deliveryRequestGroup, setDeliveryRequestGroup]);
    var willPostDeliveryRequestGroup = React.useMemo(function () {
        var itemsAndRequestDates = deliveryRequestGroup.itemsAndRequestDates.filter(function (item) {
            return deliveryRequestFormType === 'oneTime'
                ? item.key === 'oneTime'
                : item.key !== 'oneTime';
        });
        return __assign(__assign({}, deliveryRequestGroup), { itemsAndRequestDates: itemsAndRequestDates });
    }, [deliveryRequestFormType, deliveryRequestGroup]);
    var paymentTimingExplanation = usePaymentTimingExplanation(formik.values.payment_timing, offerList);
    return (React.createElement(Form, { onSubmit: formik.handleSubmit, className: isValidOrder ? '' : 'hide', ref: orderFormRef },
        React.createElement(Container, { maxWidth: "md" },
            React.createElement(Grid, { container: true, spacing: 4 },
                onShowFormTypeChange && (React.createElement(Grid, { item: true, xs: 12 },
                    React.createElement(OrderFormStepper, { activeStep: showFormType, steps: FormTypeValues }))),
                showFormType === 'order' ? (React.createElement(React.Fragment, null,
                    React.createElement(Grid, { item: true, xs: 12 },
                        React.createElement(StyledFormikSelectField, { fullWidth: true, name: "payment_timing", label: t('params.order.paymentTiming'), items: paymentTimingItems, InputProps: {
                                sx: {
                                    '&.MuiInputBase-root > .MuiSelect-select': {
                                        whiteSpace: 'pre-wrap',
                                    },
                                },
                            }, SelectProps: {
                                MenuProps: {
                                    MenuListProps: {
                                        sx: {
                                            '.MuiMenuItem-root': {
                                                whiteSpace: 'pre-wrap',
                                            },
                                        },
                                    },
                                },
                            }, formik: formik }),
                        React.createElement(ExplanationText, null, paymentTimingExplanation)),
                    itemType !== 'TypicaStockItem' && (React.createElement(Grid, { item: true, xs: 12 },
                        React.createElement(FormikSelectField, { fullWidth: true, name: "delivery_method", label: t('params.order.deliveryMethod'), items: deliveryMethodItems, required: true, formik: formik }),
                        React.createElement(ExplanationText, null, t('pages.roasterOrder.deliveryMethodExplanation', {
                            unitPrice: warehouseUnitPrice,
                        })))),
                    formik.values.delivery_method == 'split' && (React.createElement(Grid, { item: true, xs: 12 },
                        React.createElement(FormikTextField, { multiline: true, rows: 4, fullWidth: true, name: "delivery_comment", label: t('params.order.deliveryComment'), formik: formik }))),
                    role === 'roaster' ? (React.createElement(Grid, { item: true, xs: 12 },
                        React.createElement(RoasterAddressSelect, { type: "billing", roaster: roaster, onChange: changeAddress }))) : (React.createElement(React.Fragment, null,
                        React.createElement(Grid, { item: true, xs: 12 },
                            React.createElement(FormikTextField, { fullWidth: true, name: 'billing_name', label: t('params.order.billingName'), required: true, formik: formik })),
                        React.createElement(Grid, { item: true, xs: 12 },
                            React.createElement(FormikTextField, { fullWidth: true, name: 'store_name', label: t('params.order.storeName'), formik: formik })),
                        React.createElement(Grid, { item: true, xs: 12 },
                            React.createElement(FormikTextField, { fullWidth: true, name: 'person_name', label: t('params.order.personName'), required: true, formik: formik })),
                        i18n.language === 'ja' && (React.createElement(Grid, { item: true, xs: 12 },
                            React.createElement(FormikTextField, { fullWidth: true, name: "name_kana", label: t("params.order.nameKana"), formik: formik }))),
                        React.createElement(Grid, { item: true, xs: 12 },
                            React.createElement(FormikTextField, { fullWidth: true, name: "postal_code", inputProps: { maxLength: 9 }, label: t('params.order.postalCode'), required: true, formik: formik })),
                        ['prefecture', 'city', 'address'].map(function (field) { return (React.createElement(Grid, { item: true, xs: 12, key: field },
                            React.createElement(FormikTextField, { fullWidth: true, name: field, label: t("params.order.".concat(camelize(field))), required: true, formik: formik }))); }),
                        React.createElement(Grid, { item: true, xs: 12 },
                            React.createElement(FormikTextField, { fullWidth: true, name: "building", label: t('params.order.building'), formik: formik }),
                            React.createElement(Typography, { variant: "caption" }, t('pages.roasterOrder.addressExplanation'))),
                        React.createElement(Grid, { item: true, xs: 12, sm: 6, md: 3 },
                            React.createElement(FormikSelectCountryCallingCodeField, { name: "country_calling_code", label: t('params.order.countryCallingCode'), required: true, formik: formik })),
                        React.createElement(Grid, { item: true, xs: 12, sm: 6, md: 9 },
                            React.createElement(FormikTextField, { fullWidth: true, name: "phone", label: t('params.order.phone'), required: true, formik: formik })),
                        React.createElement(Grid, { item: true, xs: 12 },
                            React.createElement(FormikTextField, { fullWidth: true, name: "email", label: t('params.order.email'), required: true, formik: formik }),
                            React.createElement(Typography, { variant: "caption" }, t('pages.roasterOrder.emailExplanation'))))),
                    roaster.country.eoriNumberEnabled && (React.createElement(Grid, { item: true, xs: 12 },
                        React.createElement(FormikTextField, { fullWidth: true, name: "eori_number", label: t('params.eoriNumber'), formik: formik }))),
                    React.createElement(Grid, { item: true, xs: 12 },
                        React.createElement(FormikTextField, { multiline: true, rows: 4, fullWidth: true, name: "comment", label: t('params.order.comment'), formik: formik }),
                        React.createElement(Typography, { variant: "caption" }, t('pages.roasterOrder.deliveryTimeEtcExplanation'))),
                    contributableOkagesamaNoMori &&
                        (simulatedOrderPrice === null || simulatedOrderPrice === void 0 ? void 0 : simulatedOrderPrice.okagesamaNoMoriPriceUsd) &&
                        (simulatedOrderPrice === null || simulatedOrderPrice === void 0 ? void 0 : simulatedOrderPrice.okagesamaNoMoriPrice) && (React.createElement(Grid, { item: true, xs: 12 },
                        React.createElement("div", null,
                            React.createElement(FormikSingleCheckbox, { label: React.createElement("a", { href: okagesamaNoMoriUrls[i18n.language], target: "_blank", rel: "noreferrer" }, t('params.order.contributeToOkagesamaNoMori')), name: "contribute_to_okagesama_no_mori", formik: formik })),
                        React.createElement("div", null,
                            React.createElement(Typography, { variant: "caption" }, t('pages.roasterOrder.okagesamaNoMoriPrice', {
                                priceUsd: formatMoneyAmount(simulatedOrderPrice.okagesamaNoMoriPriceUsd, 'USD'),
                                price: formatMoneyAmount(simulatedOrderPrice.okagesamaNoMoriPrice, simulatedOrderPrice.currency),
                            }))),
                        React.createElement("div", null,
                            React.createElement(Typography, { variant: "caption" }, t('pages.roasterOrder.okagesamaNoMoriPriceText'))))),
                    React.createElement(Grid, { item: true, sm: 12 }))) : (React.createElement(React.Fragment, null,
                    React.createElement(Grid, { item: true, xs: 12 },
                        React.createElement(RoasterAddressSelect, { type: "delivery", roaster: roaster, onChange: function (address) {
                                return setDeliveryRequestGroup(__assign(__assign({}, deliveryRequestGroup), { address: address }));
                            } })),
                    React.createElement(Grid, { item: true, xs: 12 },
                        React.createElement(DeliveryRequestItemsAndRequestDateForm, { roaster: roaster, deliveryRequestFormType: deliveryRequestFormType, deliveryRequestItemsAndRequestDates: deliveryRequestGroup.itemsAndRequestDates, requestItemsWithRequestableQuantity: requestItemsWithRequestableQuantity || [], onChangeDeliveryRequestFormType: setDeliveryRequestFormType, addDeliveryRequestItemsAndRequestDate: handleAddDeliveryRequestItemsAndRequestDate, updateDeliveryRequestItemsAndRequestDate: handleUpdateDeliveryRequestItemsAndRequestDate, deleteDeliveryRequestItemsAndRequestDate: handleDeleteDeliveryRequestItemsAndRequestDate })),
                    roaster.isOfficeEurope && (React.createElement(Grid, { item: true, xs: 12 },
                        React.createElement(TextField, { fullWidth: true, name: "vat_number", label: t('params.deliveryRequest.vatNumber'), value: deliveryRequestGroup.vatNumber, onChange: function (e) {
                                setDeliveryRequestGroup(__assign(__assign({}, deliveryRequestGroup), { vatNumber: e.target.value }));
                            }, error: !deliveryRequestGroup.vatNumber, helperText: deliveryRequestGroup.vatNumber
                                ? undefined
                                : t('errors.isRequired', {
                                    field: t('params.deliveryRequest.vatNumber'),
                                }) }))),
                    React.createElement(Grid, { item: true, xs: 12 },
                        React.createElement(TextField, { fullWidth: true, name: "campaign_code", label: t('params.campaign.code'), value: deliveryRequestGroup.campaignCode, onChange: function (e) {
                                setDeliveryRequestGroup(__assign(__assign({}, deliveryRequestGroup), { campaignCode: e.target.value }));
                            } }))))),
            showFormType === 'order' && onShowFormTypeChange ? (React.createElement(React.Fragment, null,
                React.createElement(Box, { mt: 2 },
                    React.createElement(Grid, { container: true, spacing: 2 },
                        React.createElement(Grid, { item: true, sm: 4 }),
                        React.createElement(Grid, { item: true, xs: 12, sm: 4 },
                            React.createElement(Button, { fullWidth: true, disabled: !formik.isValid || !isValidOrder, onClick: function () {
                                    var _a;
                                    onShowFormTypeChange('deliveryRequest');
                                    (_a = orderFormRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView();
                                } }, t('buttons.next'))))))) : (React.createElement(React.Fragment, null,
                React.createElement(PageSubmitButton, { className: secondsToStartOrder > 0 ? 'countdown' : '', disabled: !formik.isValid || !isValidOrder || !deliveryRequestGroupIsValid, tooltip: invalidDeliveryRequestGroupTooltip, tooltipProps: { placement: 'top-start' } }, secondsToStartOrder > 0
                    ? t('pages.roasterOrder.countdownToOrder', { countdown: countdown })
                    : t('pages.roasterOrder.order')),
                onShowFormTypeChange && (React.createElement(Box, { mt: 2 },
                    React.createElement(Grid, { container: true, spacing: 2 },
                        React.createElement(Grid, { item: true, sm: 4 }),
                        React.createElement(Grid, { item: true, xs: 12, sm: 4 },
                            React.createElement(Button, { fullWidth: true, onClick: function () {
                                    var _a;
                                    onShowFormTypeChange('order');
                                    (_a = orderFormRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView();
                                }, sx: { mt: 1 } }, t('links.back'))),
                        React.createElement(Grid, { item: true, sm: 4 })))))),
            React.createElement(ConfirmationDialog, { open: confirmDialogOpen, onClose: function () { return setConfirmDialogOpen(false); }, role: role, roaster: roaster, itemType: itemType, requestItems: requestItems, requestBids: requestBids, deliveryRequestGroup: willPostDeliveryRequestGroup, values: formik.values }))));
};
export default OrderForm;
var templateObject_1, templateObject_2, templateObject_3;
